'use client';
import { useQuery, useSuspenseQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { REVIEW_QUERIES } from '../../infrastructure/review/utils/queries';
import { REVIEW_MUTATIONS } from '../../infrastructure/review/utils/mutations';
import type {
  CuratedReviewsQueryParams,
  MyReviewsQueryParams,
  ProductReviewsQueryParams,
  ReviewCreditAmountsQueryParams,
  ReviewCreditSummaryQueryParams,
  ReviewStatisticsQueryParams,
} from '../../model/review/review';

export const useReviewService = () => {
  const queryClient = useQueryClient();

  const useGetProductReviews = (productId: number, params?: Omit<ProductReviewsQueryParams, 'productId'>) => {
    return useQuery({
      ...REVIEW_QUERIES.getProductReviews(productId, params),
      enabled: Boolean(productId),
    });
  };

  const useGetAIProductReviews = (productId: number, params?: Omit<ProductReviewsQueryParams, 'productId'>) => {
    return useQuery({
      ...REVIEW_QUERIES.getAIProductReviews(productId, params),
    });
  };

  const useGetCuratedReviews = (params?: CuratedReviewsQueryParams) => {
    return useSuspenseQuery(REVIEW_QUERIES.getCuratedReviews(params));
  };

  // Gated on the session: an anonymous call would answer 401 on every product page.
  const useGetMyReviews = (params: MyReviewsQueryParams, isSignedIn: boolean) => {
    return useQuery({
      ...REVIEW_QUERIES.getMyReviews(params),
      enabled: isSignedIn,
    });
  };

  const useGetReviewCreditAmounts = (params?: ReviewCreditAmountsQueryParams) => {
    return useQuery(REVIEW_QUERIES.getReviewCreditAmounts(params));
  };

  const useSuspenseReviewCreditAmounts = (params?: ReviewCreditAmountsQueryParams) => {
    return useSuspenseQuery(REVIEW_QUERIES.getReviewCreditAmounts(params));
  };

  // Per customer, so both 401 without a session. Not retried — a 401 is the answer.
  const useGetReviewCreditEligibility = (productId: number | undefined, isSignedIn: boolean) => {
    return useQuery({
      ...REVIEW_QUERIES.getReviewCreditEligibility(productId ?? 0),
      enabled: isSignedIn && Boolean(productId),
      retry: false,
    });
  };

  const useGetReviewCreditSummary = (params: ReviewCreditSummaryQueryParams, isSignedIn: boolean) => {
    return useQuery({
      ...REVIEW_QUERIES.getReviewCreditSummary(params),
      enabled: isSignedIn,
      retry: false,
    });
  };

  const useGetReviewStatistics = (params: ReviewStatisticsQueryParams) => {
    return useQuery({
      ...REVIEW_QUERIES.getReviewStatistics(params),
      enabled: Boolean(params.productIds),
    });
  };

  const useCreateReview = () => {
    return useMutation(REVIEW_MUTATIONS.createReview(queryClient));
  };

  const useUpdateReview = () => {
    return useMutation(REVIEW_MUTATIONS.updateReview(queryClient));
  };

  return {
    useGetProductReviews,
    useGetAIProductReviews,
    useGetCuratedReviews,
    useGetMyReviews,
    useGetReviewCreditAmounts,
    useSuspenseReviewCreditAmounts,
    useGetReviewCreditEligibility,
    useGetReviewCreditSummary,
    useGetReviewStatistics,
    useCreateReview,
    useUpdateReview,
  };
};
